/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `monitoring` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class MonitoringMesg : Mesg {
    public constructor() : super("Monitoring", Profile.MesgNum.MONITORING)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Must align to logging interval, for example, time must be 00:00:00 for daily log. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** device_index. Associates this data to device_info message. Not required for file with single device (sensor). */
    public var deviceIndex: UByte?
        get() = getFieldValue(DEVICE_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVICE_INDEX_FIELD_NUM, value)

    /** calories, in kcal. Accumulated total calories. Maintained by MonitoringReader for each activity_type. See SDK documentation */
    public var calories: UShort?
        get() = getFieldValue(CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(CALORIES_FIELD_NUM, value)

    /** distance, in m. Accumulated distance. Maintained by MonitoringReader for each activity_type. See SDK documentation. */
    public var distance: Double?
        get() = getFieldValue(DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(DISTANCE_FIELD_NUM, value)

    /** cycles, in cycles. Accumulated cycles. Maintained by MonitoringReader for each activity_type. See SDK documentation. */
    public var cycles: Double?
        get() = getFieldValue(CYCLES_FIELD_NUM) as? Double
        set(value) = setFieldValue(CYCLES_FIELD_NUM, value)

    /**
     * steps: what cycles means when this message selects it.
     */
    public val steps: UInt?
        get() = getFieldValue(CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("Steps")) as? UInt

    public fun setSteps(value: UInt?) {
        setFieldValue(CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("Steps"))
    }

    /**
     * strokes: what cycles means when this message selects it.
     */
    public val strokes: Double?
        get() = getFieldValue(CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("Strokes")) as? Double

    public fun setStrokes(value: Double?) {
        setFieldValue(CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("Strokes"))
    }

    /** active_time, in s. */
    public var activeTime: Double?
        get() = getFieldValue(ACTIVE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(ACTIVE_TIME_FIELD_NUM, value)

    /** activity_type. */
    public var activityType: ActivityType?
        get() = (getFieldValue(ACTIVITY_TYPE_FIELD_NUM) as? UByte)?.let { ActivityType.fromValue(it) }
        set(value) = setFieldValue(ACTIVITY_TYPE_FIELD_NUM, value?.value)

    /** activity_subtype. */
    public var activitySubtype: ActivitySubtype?
        get() = (getFieldValue(ACTIVITY_SUBTYPE_FIELD_NUM) as? UByte)?.let { ActivitySubtype.fromValue(it) }
        set(value) = setFieldValue(ACTIVITY_SUBTYPE_FIELD_NUM, value?.value)

    /** activity_level. */
    public var activityLevel: ActivityLevel?
        get() = (getFieldValue(ACTIVITY_LEVEL_FIELD_NUM) as? UByte)?.let { ActivityLevel.fromValue(it) }
        set(value) = setFieldValue(ACTIVITY_LEVEL_FIELD_NUM, value?.value)

    /** distance_16, in 100 * m. */
    public var distance16: UShort?
        get() = getFieldValue(DISTANCE_16_FIELD_NUM) as? UShort
        set(value) = setFieldValue(DISTANCE_16_FIELD_NUM, value)

    /** cycles_16, in 2 * cycles (steps). */
    public var cycles16: UShort?
        get() = getFieldValue(CYCLES_16_FIELD_NUM) as? UShort
        set(value) = setFieldValue(CYCLES_16_FIELD_NUM, value)

    /** active_time_16, in s. */
    public var activeTime16: UShort?
        get() = getFieldValue(ACTIVE_TIME_16_FIELD_NUM) as? UShort
        set(value) = setFieldValue(ACTIVE_TIME_16_FIELD_NUM, value)

    /** local_timestamp. Must align to logging interval, for example, time must be 00:00:00 for daily log. */
    public var localTimestamp: Instant?
        get() = (getFieldValue(LOCAL_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(LOCAL_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** temperature, in C. Avg temperature during the logging interval ended at timestamp */
    public var temperature: Double?
        get() = getFieldValue(TEMPERATURE_FIELD_NUM) as? Double
        set(value) = setFieldValue(TEMPERATURE_FIELD_NUM, value)

    /** temperature_min, in C. Min temperature during the logging interval ended at timestamp */
    public var temperatureMin: Double?
        get() = getFieldValue(TEMPERATURE_MIN_FIELD_NUM) as? Double
        set(value) = setFieldValue(TEMPERATURE_MIN_FIELD_NUM, value)

    /** temperature_max, in C. Max temperature during the logging interval ended at timestamp */
    public var temperatureMax: Double?
        get() = getFieldValue(TEMPERATURE_MAX_FIELD_NUM) as? Double
        set(value) = setFieldValue(TEMPERATURE_MAX_FIELD_NUM, value)

    /** Number of values stored in activity_time. */
    public val numActivityTime: Int
        get() = getField(ACTIVITY_TIME_FIELD_NUM)?.numValues ?: 0

    /** activity_time, in minutes. Indexed using minute_activity_level enum */
    public var activityTime: List<UShort?>?
        get() {
            val field = getField(ACTIVITY_TIME_FIELD_NUM) ?: return null
            return List(field.numValues) { getActivityTime(it) }
        }
        set(value) {
            removeField(ACTIVITY_TIME_FIELD_NUM)
            value?.forEachIndexed { index, element -> setActivityTime(index, element) }
        }

    public fun getActivityTime(index: Int): UShort? =
        getFieldValue(ACTIVITY_TIME_FIELD_NUM, index) as? UShort

    public fun setActivityTime(index: Int, value: UShort?) {
        setFieldValue(ACTIVITY_TIME_FIELD_NUM, value, index)
    }

    /** active_calories, in kcal. */
    public var activeCalories: UShort?
        get() = getFieldValue(ACTIVE_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(ACTIVE_CALORIES_FIELD_NUM, value)

    /** current_activity_type_intensity. Indicates single type / intensity for duration since last monitoring message. */
    public var currentActivityTypeIntensity: UByte?
        get() = getFieldValue(CURRENT_ACTIVITY_TYPE_INTENSITY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(CURRENT_ACTIVITY_TYPE_INTENSITY_FIELD_NUM, value)

    /** timestamp_min_8, in min. */
    public var timestampMin8: UByte?
        get() = getFieldValue(TIMESTAMP_MIN_8_FIELD_NUM) as? UByte
        set(value) = setFieldValue(TIMESTAMP_MIN_8_FIELD_NUM, value)

    /** timestamp_16, in s. */
    public var timestamp16: UShort?
        get() = getFieldValue(TIMESTAMP_16_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_16_FIELD_NUM, value)

    /** heart_rate, in bpm. */
    public var heartRate: UByte?
        get() = getFieldValue(HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(HEART_RATE_FIELD_NUM, value)

    /** intensity. */
    public var intensity: Double?
        get() = getFieldValue(INTENSITY_FIELD_NUM) as? Double
        set(value) = setFieldValue(INTENSITY_FIELD_NUM, value)

    /** duration_min, in min. */
    public var durationMin: UShort?
        get() = getFieldValue(DURATION_MIN_FIELD_NUM) as? UShort
        set(value) = setFieldValue(DURATION_MIN_FIELD_NUM, value)

    /** duration, in s. */
    public var duration: UInt?
        get() = getFieldValue(DURATION_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DURATION_FIELD_NUM, value)

    /** ascent, in m. */
    public var ascent: Double?
        get() = getFieldValue(ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(ASCENT_FIELD_NUM, value)

    /** descent, in m. */
    public var descent: Double?
        get() = getFieldValue(DESCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(DESCENT_FIELD_NUM, value)

    /** moderate_activity_minutes, in minutes. */
    public var moderateActivityMinutes: UShort?
        get() = getFieldValue(MODERATE_ACTIVITY_MINUTES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MODERATE_ACTIVITY_MINUTES_FIELD_NUM, value)

    /** vigorous_activity_minutes, in minutes. */
    public var vigorousActivityMinutes: UShort?
        get() = getFieldValue(VIGOROUS_ACTIVITY_MINUTES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(VIGOROUS_ACTIVITY_MINUTES_FIELD_NUM, value)

    /** pushes. Wheelchair Pushes */
    public var pushes: UInt?
        get() = getFieldValue(PUSHES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(PUSHES_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val DEVICE_INDEX_FIELD_NUM: UByte = 0u
        public const val CALORIES_FIELD_NUM: UByte = 1u
        public const val DISTANCE_FIELD_NUM: UByte = 2u
        public const val CYCLES_FIELD_NUM: UByte = 3u
        public const val ACTIVE_TIME_FIELD_NUM: UByte = 4u
        public const val ACTIVITY_TYPE_FIELD_NUM: UByte = 5u
        public const val ACTIVITY_SUBTYPE_FIELD_NUM: UByte = 6u
        public const val ACTIVITY_LEVEL_FIELD_NUM: UByte = 7u
        public const val DISTANCE_16_FIELD_NUM: UByte = 8u
        public const val CYCLES_16_FIELD_NUM: UByte = 9u
        public const val ACTIVE_TIME_16_FIELD_NUM: UByte = 10u
        public const val LOCAL_TIMESTAMP_FIELD_NUM: UByte = 11u
        public const val TEMPERATURE_FIELD_NUM: UByte = 12u
        public const val TEMPERATURE_MIN_FIELD_NUM: UByte = 14u
        public const val TEMPERATURE_MAX_FIELD_NUM: UByte = 15u
        public const val ACTIVITY_TIME_FIELD_NUM: UByte = 16u
        public const val ACTIVE_CALORIES_FIELD_NUM: UByte = 19u
        public const val CURRENT_ACTIVITY_TYPE_INTENSITY_FIELD_NUM: UByte = 24u
        public const val TIMESTAMP_MIN_8_FIELD_NUM: UByte = 25u
        public const val TIMESTAMP_16_FIELD_NUM: UByte = 26u
        public const val HEART_RATE_FIELD_NUM: UByte = 27u
        public const val INTENSITY_FIELD_NUM: UByte = 28u
        public const val DURATION_MIN_FIELD_NUM: UByte = 29u
        public const val DURATION_FIELD_NUM: UByte = 30u
        public const val ASCENT_FIELD_NUM: UByte = 31u
        public const val DESCENT_FIELD_NUM: UByte = 32u
        public const val MODERATE_ACTIVITY_MINUTES_FIELD_NUM: UByte = 33u
        public const val VIGOROUS_ACTIVITY_MINUTES_FIELD_NUM: UByte = 34u
        public const val PUSHES_FIELD_NUM: UByte = 41u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("DeviceIndex", 0u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[1] = Field("Calories", 1u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[2] = Field("Distance", 2u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[3] = Field("Cycles", 3u, BaseType.UINT32, 2.0, 0.0, "cycles", false)
                .addSubField(
                    SubField("Steps", BaseType.UINT32, 1.0, 0.0, "steps").also { subField ->
                        subField.addMap(5u, 6)
                        subField.addMap(5u, 1)
                    },
                )
                .addSubField(
                    SubField("Strokes", BaseType.UINT32, 2.0, 0.0, "strokes").also { subField ->
                        subField.addMap(5u, 2)
                        subField.addMap(5u, 5)
                    },
                )
            fields[4] = Field("ActiveTime", 4u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[5] = Field("ActivityType", 5u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[6] = Field("ActivitySubtype", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[7] = Field("ActivityLevel", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("Distance16", 8u, BaseType.UINT16, 1.0, 0.0, "100 * m", false)
            fields[9] = Field("Cycles16", 9u, BaseType.UINT16, 1.0, 0.0, "2 * cycles (steps)", false)
            fields[10] = Field("ActiveTime16", 10u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[11] = Field("LocalTimestamp", 11u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[12] = Field("Temperature", 12u, BaseType.SINT16, 100.0, 0.0, "C", false)
            fields[14] = Field("TemperatureMin", 14u, BaseType.SINT16, 100.0, 0.0, "C", false)
            fields[15] = Field("TemperatureMax", 15u, BaseType.SINT16, 100.0, 0.0, "C", false)
            fields[16] = Field("ActivityTime", 16u, BaseType.UINT16, 1.0, 0.0, "minutes", false)
            fields[19] = Field("ActiveCalories", 19u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[24] = Field("CurrentActivityTypeIntensity", 24u, BaseType.BYTE, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(5u, false, 5, 1.0, 0.0))
                .addComponent(FieldComponent(28u, false, 3, 1.0, 0.0))
            fields[25] = Field("TimestampMin8", 25u, BaseType.UINT8, 1.0, 0.0, "min", false)
            fields[26] = Field("Timestamp16", 26u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[27] = Field("HeartRate", 27u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[28] = Field("Intensity", 28u, BaseType.UINT8, 10.0, 0.0, "", false)
            fields[29] = Field("DurationMin", 29u, BaseType.UINT16, 1.0, 0.0, "min", false)
            fields[30] = Field("Duration", 30u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[31] = Field("Ascent", 31u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[32] = Field("Descent", 32u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[33] = Field("ModerateActivityMinutes", 33u, BaseType.UINT16, 1.0, 0.0, "minutes", false)
            fields[34] = Field("VigorousActivityMinutes", 34u, BaseType.UINT16, 1.0, 0.0, "minutes", false)
            fields[41] = Field("Pushes", 41u, BaseType.UINT32, 1.0, 0.0, "", false)
            return fields
        }
    }
}
